// =============================================================================
// FILE: packages/renderer/src/components/dashboard/WinsFeed.tsx
// PURPOSE: RCD² Growth Agent — "Wins" feed panel.
//
//   Monitors audit-trail events from DASHBOARD_RECENT_ACTIVITY and reframes
//   them as system successes ("wins") with a positive, action-oriented tone
//   aligned with Emmy's persona: concise, courteous, professional.
//
//   No new backend required — pure frontend transformation of existing data.
// =============================================================================

import { useMemo, useRef, useEffect, type JSX } from 'react';
import { Trophy, Star, CheckCircle2, Zap, Database, Users, FolderOpen, Shield, FileBarChart2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { call } from '../../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { growthAgent } from '../../stores/growthAgent.store.js';
import { inferEventType } from '../../services/growthAgent.js';

type ActivityEntry = { timestamp: string; tableName: string; operation: string; recordId: string };

interface Win {
  id:        string;
  icon:      typeof Trophy;
  iconColor: string;
  title:     string;
  detail:    string;
  ts:        string;
  tier:      'milestone' | 'progress' | 'routine';
}

// ─── Transformation map ───────────────────────────────────────────────────────

function toWin(entry: ActivityEntry, idx: number): Win | null {
  const { tableName, operation, timestamp } = entry;
  const op = operation.toUpperCase();

  const base: Pick<Win, 'id' | 'ts'> = {
    id: `${timestamp}-${idx}`,
    ts: new Date(timestamp).toLocaleTimeString([], { hour12: true, hour: '2-digit', minute: '2-digit' }),
  };

  if (op === 'INSERT') {
    switch (tableName) {
      case 'projects':
        return { ...base, icon: Star,         iconColor: '#25B5A8', title: 'New project started',        detail: 'A fresh project is live in the system.',          tier: 'milestone' };
      case 'assets':
        return { ...base, icon: Zap,          iconColor: '#7AE0D6', title: 'Device added',               detail: 'Asset inventory expanded.',                       tier: 'progress' };
      case 'racks':
        return { ...base, icon: Database,     iconColor: '#34D399', title: 'Rack configured',            detail: 'New rack registered in the digital twin.',         tier: 'progress' };
      case 'contacts':
        return { ...base, icon: Users,        iconColor: '#818CF8', title: 'Contact onboarded',          detail: 'Network is growing.',                             tier: 'routine' };
      case 'organizations':
        return { ...base, icon: Users,        iconColor: '#A78BFA', title: 'Organization added',         detail: 'Another org in the registry.',                    tier: 'progress' };
      case 'document_register':
        return { ...base, icon: FolderOpen,   iconColor: '#F9A8D4', title: 'Document filed',             detail: 'Record saved to the document register.',          tier: 'routine' };
      case 'clash_tests':
        return { ...base, icon: Shield,       iconColor: '#FCD34D', title: 'Clash test created',         detail: 'Proactive conflict detection is running.',        tier: 'progress' };
      case 'clash_results':
        return { ...base, icon: CheckCircle2, iconColor: '#6EE7B7', title: 'Clash resolved',             detail: 'Design integrity confirmed.',                     tier: 'milestone' };
      case 'meetings':
        return { ...base, icon: CheckCircle2, iconColor: '#7AE0D6', title: 'Meeting captured',           detail: 'Session logged and saved.',                       tier: 'routine' };
      case 'buildings':
        return { ...base, icon: Database,     iconColor: '#25B5A8', title: 'Building added',             detail: 'Spatial model extended.',                         tier: 'progress' };
      default:
        return null; // ignore unknown tables
    }
  }

  if (op === 'UPDATE') {
    switch (tableName) {
      case 'projects':
        return { ...base, icon: Trophy, iconColor: '#25B5A8', title: 'Project milestone advanced',       detail: 'Project record updated.',                         tier: 'progress' };
      case 'assets':
        return { ...base, icon: Zap,    iconColor: '#7AE0D6', title: 'Device record updated',            detail: 'Asset information refined.',                      tier: 'routine' };
      default:
        return null;
    }
  }

  return null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

function useWins() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'recentActivity', 'wins'],
    queryFn:  async () => {
      const res = await call<{ limit: number }, ActivityEntry[]>(IPC.DASHBOARD_RECENT_ACTIVITY, { limit: 30 });
      return res ?? [];
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const wins = useMemo<Win[]>(() => {
    if (!data) return [];
    return data
      .map((entry, idx) => toWin(entry, idx))
      .filter((w): w is Win => w !== null)
      .slice(0, 8);
  }, [data]);

  // Auto-trigger Growth Agent when new wins arrive
  const prevCountRef = useRef<number>(-1);
  useEffect(() => {
    if (!wins.length) return;
    if (prevCountRef.current === -1) {
      prevCountRef.current = wins.length;
      return;
    }
    if (wins.length > prevCountRef.current) {
      const newest = wins[0];
      if (newest) {
        const entry = data?.find((_, i) => i === 0);
        if (entry) {
          const eventType = inferEventType(entry.tableName, entry.operation) ?? 'generic_win';
          growthAgent.celebrate(eventType);
        }
      }
    }
    prevCountRef.current = wins.length;
  }, [wins.length]);

  return { wins, isLoading };
}

// ─── Win item ─────────────────────────────────────────────────────────────────

function WinItem({ win }: { win: Win }): JSX.Element {
  const tierStyles: Record<Win['tier'], string> = {
    milestone: 'border-l-2 pl-3',
    progress:  'border-l pl-3',
    routine:   'pl-4',
  };
  const tierBorderColor: Record<Win['tier'], string> = {
    milestone: win.iconColor,
    progress:  win.iconColor + '99',
    routine:   'transparent',
  };

  return (
    <div
      className={`flex items-start gap-2.5 py-2 ${tierStyles[win.tier]}`}
      style={{ borderColor: tierBorderColor[win.tier] }}
    >
      <div
        className="flex items-center justify-center w-6 h-6 rounded-sm shrink-0 mt-0.5"
        style={{ background: win.iconColor + '18' }}
      >
        <win.icon className="h-3.5 w-3.5" style={{ color: win.iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[12px] font-medium text-zinc-200 truncate">{win.title}</span>
          <span className="text-[10px] text-zinc-600 font-mono shrink-0">{win.ts}</span>
        </div>
        <div className="text-[10.5px] text-zinc-500 mt-0.5">{win.detail}</div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function WinsFeed({ onNavigate }: { onNavigate?: (route: string) => void }): JSX.Element {
  const { wins, isLoading } = useWins();

  return (
    <div
      data-testid="wins-feed"
      className="rounded-sm overflow-hidden"
      style={{
        border:     '1px solid rgba(37,181,168,0.2)',
        background: 'rgba(7,18,14,0.6)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-2.5 border-b"
        style={{ borderColor: 'rgba(37,181,168,0.15)' }}
      >
        <Trophy className="h-3.5 w-3.5 text-teal-400" />
        <span className="text-[10px] uppercase tracking-[0.24em] text-teal-300/80 font-medium">
          System Wins
        </span>
        <span
          className="ml-auto text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
          style={{ background: 'rgba(37,181,168,0.1)', color: 'rgba(122,224,214,0.6)' }}
        >
          Growth Agent
        </span>
      </div>

      {/* Feed */}
      <div className="px-4 py-1 divide-y divide-zinc-800/50">
        {isLoading ? (
          <div className="py-6 text-center text-[11px] text-zinc-600">
            Analysing system events…
          </div>
        ) : wins.length === 0 ? (
          <div className="py-6 text-center">
            <div className="text-[11px] text-zinc-500 mb-1">No wins recorded yet.</div>
            <div className="text-[10px] text-zinc-700">
              Start by creating a project or adding your first device.
            </div>
          </div>
        ) : (
          wins.map((win) => <WinItem key={win.id} win={win} />)
        )}
      </div>

      {/* Footer: Emmy quote + Generate Report button */}
      <div
        className="px-4 py-2.5 border-t flex items-center gap-2"
        style={{ borderColor: 'rgba(37,181,168,0.1)' }}
      >
        <div className="flex-1 text-[10px] text-zinc-600 italic truncate">
          Emmy: "Every action counts. You&rsquo;re building something excellent."
        </div>
        {onNavigate && (
          <button
            type="button"
            data-testid="wins-feed-generate-report-btn"
            onClick={() => {
              growthAgent.celebrate('generic_win');
              onNavigate('auto-report');
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 5, cursor: 'pointer',
              background: 'rgba(37,181,168,0.1)',
              border: '1px solid rgba(37,181,168,0.28)',
              color: '#5EEAD4', fontSize: 9, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              flexShrink: 0,
            }}
            onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(37,181,168,0.2)'; }}
            onMouseOut={(e)  => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(37,181,168,0.1)'; }}
          >
            <FileBarChart2 style={{ width: 10, height: 10 }} />
            Report
          </button>
        )}
      </div>
    </div>
  );
}
